/**
 * DatePicker — popover de selecao de data (estilo do mockup aprovado).
 *
 * Esquerda: atalhos rapidos (Hoje, Amanha, Este final de semana, Semana que
 * vem, Proximo final de semana, 2 semanas, 4 semanas). Direita: calendario do
 * mes navegavel. Rodape: toggle "Ignorar dias nao uteis" (quando ligado, os
 * atalhos de dia util pulam fins de semana / feriados).
 *
 * Uso (popover ancorado): o pai renderiza dentro de um wrapper position:relative
 *   {open && <DatePicker value={prazo} onSelect={d=>...} onClose={()=>setOpen(false)} />}
 *
 * onSelect recebe um Date (meio-dia local) ou null (limpar prazo). O pai e'
 * responsavel por fechar (chamar onClose) — ja chamado apos onSelect aqui.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  addDays,
  addMonthsToDate,
  buildMonthGrid,
  formatMonthLabel,
  isSameDay,
  startOfDayLocal,
} from "../Calendar/calendarGrid";
import { isDiaUtil } from "../../utils/calculos";

import * as S from "./styles";

const WEEKDAYS = ["do", "2ª", "3ª", "4ª", "5ª", "6ª", "sáb"];
const DOW = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MES_ABREV = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function proximoDiaUtil(d) {
  let x = startOfDayLocal(d);
  let guard = 0;
  while (!isDiaUtil(x) && guard < 14) {
    x = addDays(x, 1);
    guard += 1;
  }
  return x;
}

// proximo sabado a partir de `base` (inclui hoje se ja for sabado)
function proximoSabado(base) {
  const x = startOfDayLocal(base);
  const add = (6 - x.getDay() + 7) % 7;
  return addDays(x, add);
}

// proxima segunda-feira (sempre futura)
function proximaSegunda(base) {
  const x = startOfDayLocal(base);
  const add = (8 - x.getDay()) % 7 || 7;
  return addDays(x, add);
}

export default function DatePicker({ value, onSelect, onClose, anchorRef }) {
  const ref = useRef(null);
  const hoje = useMemo(() => startOfDayLocal(new Date()), []);
  const [cursor, setCursor] = useState(() => startOfDayLocal(value || new Date()));
  const [ignorar, setIgnorar] = useState(true);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // posiciona o popover (fixed) ancorado ao trigger; reposiciona em scroll/resize.
  useLayoutEffect(() => {
    function reposicionar() {
      const a = anchorRef?.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      const W = Math.min(window.innerWidth * 0.92, 520);
      let left = r.left;
      if (left + W > window.innerWidth - 8) left = window.innerWidth - W - 8;
      if (left < 8) left = 8;
      let top = r.bottom + 6;
      const H = 380; // altura aproximada
      if (top + H > window.innerHeight - 8 && r.top - H - 6 > 8) top = r.top - H - 6;
      setPos({ top, left });
    }
    reposicionar();
    window.addEventListener("scroll", reposicionar, true);
    window.addEventListener("resize", reposicionar);
    return () => {
      window.removeEventListener("scroll", reposicionar, true);
      window.removeEventListener("resize", reposicionar);
    };
  }, [anchorRef]);

  // fecha ao clicar fora / ESC
  useEffect(() => {
    function onDown(e) {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        !anchorRef?.current?.contains(e.target)
      ) {
        onClose?.();
      }
    }
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const presets = useMemo(() => {
    const ajusta = (d) => (ignorar ? proximoDiaUtil(d) : startOfDayLocal(d));
    return [
      { id: "hoje", label: "Hoje", date: hoje },
      { id: "amanha", label: "Amanhã", date: ajusta(addDays(hoje, 1)) },
      { id: "estefds", label: "Este final de semana", date: proximoSabado(hoje) },
      { id: "semanavem", label: "Semana que vem", date: proximaSegunda(hoje) },
      { id: "proxfds", label: "Próximo final de semana", date: addDays(proximoSabado(hoje), 7) },
      { id: "2sem", label: "2 semanas", date: ajusta(addDays(hoje, 14)) },
      { id: "4sem", label: "4 semanas", date: ajusta(addDays(hoje, 28)) },
    ];
  }, [hoje, ignorar]);

  function hint(d) {
    const diff = Math.round((startOfDayLocal(d) - hoje) / 86400000);
    if (diff >= 0 && diff <= 6) return DOW[d.getDay()];
    return `${d.getDate()} ${MES_ABREV[d.getMonth()]}`;
  }

  function escolher(d) {
    // meio-dia local: evita salto de fuso ao serializar e bate com o default do form
    const base = startOfDayLocal(d);
    const comHora = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 12, 0, 0);
    onSelect(comHora);
    onClose?.();
  }

  const grid = useMemo(
    () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth(), 0),
    [cursor]
  );

  return createPortal(
    <S.Pop ref={ref} style={{ top: pos.top, left: pos.left }} onMouseDown={(e) => e.stopPropagation()}>
      <S.Cols>
        <S.Presets>
          {presets.map((p) => (
            <S.PresetRow key={p.id} type="button" onClick={() => escolher(p.date)}>
              <span>{p.label}</span>
              <span className="hint">{hint(p.date)}</span>
            </S.PresetRow>
          ))}
          {value ? (
            <S.PresetRow type="button" className="limpar" onClick={() => { onSelect(null); onClose?.(); }}>
              <span>Sem prazo</span>
              <span className="hint">limpar</span>
            </S.PresetRow>
          ) : null}
        </S.Presets>

        <S.Cal>
          <S.CalHead>
            <strong>{formatMonthLabel(cursor.getFullYear(), cursor.getMonth())}</strong>
            <S.CalNav>
              <button type="button" onClick={() => setCursor(hoje)}>Hoje</button>
              <button type="button" aria-label="Mês anterior" onClick={() => setCursor(addMonthsToDate(cursor, -1))}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
              </button>
              <button type="button" aria-label="Próximo mês" onClick={() => setCursor(addMonthsToDate(cursor, 1))}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
              </button>
            </S.CalNav>
          </S.CalHead>
          <S.WeekRow>
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </S.WeekRow>
          <S.Grid>
            {grid.map((cell) => {
              const sel = value && isSameDay(cell.date, value);
              const ehHoje = isSameDay(cell.date, hoje);
              const naoUtil = !isDiaUtil(cell.date);
              return (
                <S.Day
                  key={cell.key}
                  type="button"
                  $out={!cell.inMonth}
                  $sel={sel}
                  $hoje={ehHoje && !sel}
                  $weekend={naoUtil}
                  onClick={() => escolher(cell.date)}
                >
                  {cell.date.getDate()}
                </S.Day>
              );
            })}
          </S.Grid>
        </S.Cal>
      </S.Cols>

      <S.Foot>
        <S.Toggle
          type="button"
          role="switch"
          aria-checked={ignorar}
          $on={ignorar}
          onClick={() => setIgnorar((v) => !v)}
        >
          <span className="knob" />
        </S.Toggle>
        <span>Ignorar dias não úteis</span>
      </S.Foot>
    </S.Pop>,
    document.body
  );
}
